import { I18NextContext } from '../@types/types'

export const getLanguagePath = (
  language: string,
  languageContext: I18NextContext
) => {
  return languageContext.generateDefaultLanguagePage ||
    language !== languageContext.defaultLanguage
    ? `/${formatLang(language)}`
    : ''
}

export const formatLang = (language: string) => {
  switch (language.toLowerCase()) {
    case 'en-gb':
    case 'uk':
      return 'uk'
    case 'en-ca':
    case 'ca':
      return 'ca'
    case 'en-ie':
    case 'ie':
      return 'ie'
    case 'au':
      return 'au'
    case 'es-us':
      return 'es-us'
    case 'en-au':
    case 'au':
      return 'au'
    case 'en-us':
    case 'us':
    default:
      return ''
  }
}

// This is coming from Gatsby's internals: https://github.com/gatsbyjs/gatsby/blob/2975c4d1271e3da52b531ad2f49261c362e5ae13/packages/gatsby-link/src/index.js#L42-L46
export const isExternalLink = (path: string) =>
  path?.startsWith('http://') ||
  path?.startsWith('https://') ||
  path?.startsWith('//') ||
  path?.startsWith('mailto:')

// Paths that should never be localized
// Add path segments to this array to prevent localization for those routes
// Examples: '/shop/', '/api/', '/admin/'
const LOCALIZATION_EXCEPTIONS = [
  '/shop/',
  '/quote-request',
  '/request-retail-demo',
  '/request-demo-video',
  '/lp/xtrachef-request',
  '/payroll-demo-request',
  '/capital-demo-request',
  '/payments/payment-processing-fees',
  '/request-demo/enterprise',
  '/partners',
  '/customers/digital-ordering',
  '/customers/toast-delivery-services'
]

export const localizeLink = (
  href: string,
  language?: string,
  languageContext?: I18NextContext
) => {
  const urlLanguage = language || languageContext?.language
  // Handle POS links by converting them to relative paths first
  if (href?.startsWith('https://pos.toasttab.com')) {
    const baseUrl = 'https://pos.toasttab.com'
    const pathOnly = href.replace(baseUrl, '')

    // Don't localize if this is a page anchor link or empty path
    if (
      !pathOnly ||
      pathOnly?.startsWith('#') ||
      ['en', 'us', 'en-us'].includes(urlLanguage.toLowerCase())
    ) {
      return href
    }

    // Don't localize paths that are in the exceptions list
    if (
      LOCALIZATION_EXCEPTIONS.some((exception) => pathOnly.includes(exception))
    ) {
      return href
    }

    // Don't localize if the URL already includes the locale
    const locales = ['ca', 'ie', 'uk', 'es-us', 'au']
    let skipLocalization = false
    locales.forEach((lang) => {
      if (
        pathOnly.startsWith(`${lang}/`) ||
        pathOnly.startsWith(`/${lang}/`) ||
        pathOnly.startsWith(`/${lang}`)
      ) {
        skipLocalization = true
      }
    })

    if (skipLocalization) {
      return href
    }

    return `${baseUrl}${getLanguagePath(
      urlLanguage,
      languageContext
    )}${pathOnly}`
  }

  // Handle regular links (non-POS)
  // Don't localize if this is a page anchor link or an external link... and skip the whole thing if href doesn't exist
  if (
    !href ||
    href?.startsWith('#') ||
    isExternalLink(href) ||
    ['en', 'us', 'en-us'].includes(urlLanguage.toLowerCase())
  ) {
    return href
  }

  // Don't localize paths that are in the exceptions list
  if (LOCALIZATION_EXCEPTIONS.some((exception) => href.includes(exception))) {
    return href
  }

  // Don't localize if the URL already includes the locale
  const locales = ['ca', 'ie', 'uk', 'es-us', 'au']
  let skipLocalization = false
  locales.forEach((lang) => {
    if (href.startsWith(`${lang}/`) || href.startsWith(`/${lang}/`)) {
      skipLocalization = true
    }
  })

  // Can't return the value within the loop, so we need this extra if check here
  if (skipLocalization) {
    return href
  }
  return `${getLanguagePath(urlLanguage, languageContext)}${
    href === '/' ? '' : href
  }`
}

// Smooth scrolls to the target element
export const anchorLinkScroll = (target: string) => {
  if (typeof window !== 'undefined' && document.querySelector(target)) {
    const sectionTop =
      document.querySelector(target).getBoundingClientRect().top +
      window.scrollY -
      140

    window.scrollTo({
      top: sectionTop,
      behavior: 'smooth'
    })
  }
}

export const rewritePOSLink = (url: string, languageContext: any) => {
  const urlLanguage = languageContext.language
  // Only target pos.toasttab.com/ URLs
  if (url.startsWith('https://pos.toasttab.com')) {
    const baseUrl = 'https://pos.toasttab.com'

    // remove the domain and treat it like a relative link
    const pathOnly = url.replace(baseUrl, '')

    const localizedPath = localizeLink(pathOnly, urlLanguage, languageContext)

    // return only the localizedPath, which is now relative
    return localizedPath
  }

  return url // leave other external links untouched
}
